import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { TrendingUp, Loader2, FileText, ShoppingCart, Users } from 'lucide-react';
import axios from 'axios';
import SuperAdminLayout from '@/components/superadmin/SuperAdminLayout';

const API = process.env.REACT_APP_BACKEND_URL;
const sadApi = () => {
  const token = localStorage.getItem('sad_token');
  return axios.create({ baseURL: `${API}/api`, headers: { Authorization: `Bearer ${token}` } });
};

const SuperAdminRevenue = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchRevenue = async () => {
      try {
        const res = await sadApi().get('/sad/revenue', { params: { period: 'monthly' } });
        setData(res.data);
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    fetchRevenue();
  }, []);

  if (loading) {
    return (
      <SuperAdminLayout>
        <div className="flex items-center justify-center min-h-[400px]">
          <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
        </div>
      </SuperAdminLayout>
    );
  }

  const months = data?.data || [];
  const maxRevenue = Math.max(...months.map(m => m.invoice_revenue + m.sales_revenue), 1);

  const totalInvoiceRev = months.reduce((s, m) => s + m.invoice_revenue, 0);
  const totalSalesRev = months.reduce((s, m) => s + m.sales_revenue, 0);
  const totalNewUsers = months.reduce((s, m) => s + m.new_users, 0);

  return (
    <SuperAdminLayout>
      <div className="space-y-6" data-testid="sad-revenue-page">
        <div>
          <h1 className="text-2xl font-bold text-white">Revenue Analytics</h1>
          <p className="text-slate-400 text-sm">12-month overview</p>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
            className="bg-[#1e293b] border border-slate-700 p-5">
            <div className="flex items-center gap-2 mb-2">
              <FileText className="w-4 h-4 text-emerald-400" />
              <span className="text-slate-400 text-xs uppercase">Invoice Revenue (12m)</span>
            </div>
            <p className="text-2xl font-bold text-emerald-400">{totalInvoiceRev.toLocaleString('es-ES', { minimumFractionDigits: 2 })} EUR</p>
          </motion.div>
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}
            className="bg-[#1e293b] border border-slate-700 p-5">
            <div className="flex items-center gap-2 mb-2">
              <ShoppingCart className="w-4 h-4 text-purple-400" />
              <span className="text-slate-400 text-xs uppercase">POS Revenue (12m)</span>
            </div>
            <p className="text-2xl font-bold text-purple-400">{totalSalesRev.toLocaleString('es-ES', { minimumFractionDigits: 2 })} EUR</p>
          </motion.div>
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
            className="bg-[#1e293b] border border-slate-700 p-5">
            <div className="flex items-center gap-2 mb-2">
              <Users className="w-4 h-4 text-blue-400" />
              <span className="text-slate-400 text-xs uppercase">New Users (12m)</span>
            </div>
            <p className="text-2xl font-bold text-blue-400">{totalNewUsers}</p>
          </motion.div>
        </div>

        {/* Bar Chart */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
          className="bg-[#1e293b] border border-slate-700 p-5"
        >
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-white font-semibold flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-amber-400" />
              Monthly Revenue
            </h3>
            <div className="flex items-center gap-4 text-xs">
              <span className="flex items-center gap-1.5">
                <div className="w-3 h-3 bg-emerald-500 rounded-sm" /> Invoices
              </span>
              <span className="flex items-center gap-1.5">
                <div className="w-3 h-3 bg-purple-500 rounded-sm" /> POS Sales
              </span>
            </div>
          </div>

          <div className="flex items-end gap-2 h-48" data-testid="revenue-chart">
            {months.map((m, i) => {
              const totalRev = m.invoice_revenue + m.sales_revenue;
              const heightPercent = maxRevenue > 0 ? (totalRev / maxRevenue) * 100 : 0;
              const invPercent = totalRev > 0 ? (m.invoice_revenue / totalRev) * 100 : 50;

              return (
                <div key={i} className="flex-1 flex flex-col items-center group" data-testid={`month-bar-${i}`}>
                  {/* Tooltip */}
                  <div className="hidden group-hover:block absolute -mt-20 bg-slate-900 border border-slate-600 p-2 rounded-sm text-xs z-10 whitespace-nowrap">
                    <p className="text-white font-medium">{m.label}</p>
                    <p className="text-emerald-400">Inv: {m.invoice_revenue.toFixed(2)}</p>
                    <p className="text-purple-400">POS: {m.sales_revenue.toFixed(2)}</p>
                    <p className="text-blue-400">Users: +{m.new_users}</p>
                  </div>

                  <div className="w-full flex flex-col rounded-t-sm overflow-hidden"
                    style={{ height: `${Math.max(heightPercent, 2)}%` }}>
                    <div className="bg-emerald-500" style={{ height: `${invPercent}%` }} />
                    <div className="bg-purple-500 flex-1" />
                  </div>
                  <span className="text-[10px] text-slate-500 mt-1.5 truncate w-full text-center">
                    {m.label.split(' ')[0]}
                  </span>
                </div>
              );
            })}
          </div>
        </motion.div>

        {/* Monthly Table */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-[#1e293b] border border-slate-700 overflow-hidden"
        >
          <table className="w-full text-sm" data-testid="revenue-table">
            <thead>
              <tr className="border-b border-slate-700 bg-slate-800/50">
                <th className="px-4 py-3 text-left text-slate-400 font-medium">Month</th>
                <th className="px-4 py-3 text-right text-slate-400 font-medium">Invoice Rev</th>
                <th className="px-4 py-3 text-right text-slate-400 font-medium">Invoices</th>
                <th className="px-4 py-3 text-right text-slate-400 font-medium">POS Rev</th>
                <th className="px-4 py-3 text-right text-slate-400 font-medium">POS Sales</th>
                <th className="px-4 py-3 text-right text-slate-400 font-medium">New Users</th>
              </tr>
            </thead>
            <tbody>
              {months.map((m, i) => (
                <tr key={i} className="border-b border-slate-700/50 hover:bg-slate-800/30">
                  <td className="px-4 py-2.5 text-white">{m.label}</td>
                  <td className="px-4 py-2.5 text-right text-emerald-400 font-medium">{m.invoice_revenue.toFixed(2)}</td>
                  <td className="px-4 py-2.5 text-right text-slate-400">{m.invoice_count}</td>
                  <td className="px-4 py-2.5 text-right text-purple-400 font-medium">{m.sales_revenue.toFixed(2)}</td>
                  <td className="px-4 py-2.5 text-right text-slate-400">{m.sales_count}</td>
                  <td className="px-4 py-2.5 text-right text-blue-400">{m.new_users}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </motion.div>
      </div>
    </SuperAdminLayout>
  );
};

export default SuperAdminRevenue;
